import React, { useState, useCallback, useEffect } from 'react';
import { useSelector } from "react-redux";
import './CameraTagMapping.css';
import Button from '../../generic_components/Button/Button';
import { API } from '../../api';

const CameraTagMapping = ({ tags = [], hwid, modelId, onSubmitSuccess = null, onModalClose = null }) => {
  const [mappings, setMappings] = useState({});
  const [originalMappings, setOriginalMappings] = useState({}); // Store original to detect changes
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingMappings, setLoadingMappings] = useState(true);
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [cameras, setCameras] = useState([]);
  const devicesPerSite = useSelector(state => state.devicesPerSite);

  // Extract cameras from Redux
  useEffect(() => {
    if (!hwid || !devicesPerSite || Object.keys(devicesPerSite).length === 0) {
      setCameras([]);
      return;
    }
    let camerasList = [];

    devicesPerSite.servers?.forEach((server) => {
      server.cameras.forEach((cameraObj) => {
        camerasList.push({
          ...cameraObj,
          server_name: server.server_name
        });
      });
    });

    const uniqueCameras = [
      ...new Map(camerasList.map(cam => [cam.camera_id, cam])).values()
    ];

    setCameras(uniqueCameras);
  }, [devicesPerSite, hwid]);

  // 🔑 Load existing mappings when component mounts
  useEffect(() => {
    const loadExistingMappings = async () => {
      if (!hwid || !modelId) {
        setLoadingMappings(false);
        return;
      }

      setLoadingMappings(true);

      try {
        const response = await API.get(
          `/matterport/camera-tag-mappings/${hwid}/${modelId}`
        );

        const data = response?.data;
        
        if (data && data.mappings && data.mappings.length > 0) {
          console.log('✅ Loaded existing mappings:', data.mappings);

          // Transform existing mappings to state format
          // mappings[camera_id] = tag object
          const loadedMappings = {};

          for (const mapping of data.mappings) {
            // Find the tag object from the tags array
            const tagObject = tags.find(t => t.id === mapping.tag_id);
            
            if (tagObject) {
              loadedMappings[mapping.camera_id] = tagObject;
            } else {
              console.warn(`⚠️ Tag with ID ${mapping.tag_id} not found in tags array`);
              // Still add it with basic info so we know it's mapped
              loadedMappings[mapping.camera_id] = {
                id: mapping.tag_id,
                label: `Tag: ${mapping.tag_id}`,
              };
            }
          }

          setMappings(loadedMappings);
          setOriginalMappings(JSON.parse(JSON.stringify(loadedMappings))); // Deep copy
          console.log('📊 Mappings state set:', loadedMappings);
        } else {
          console.log('ℹ️ No existing mappings found');
          setMappings({});
          setOriginalMappings({});
        }

      } catch (error) {
        console.error('❌ Error loading mappings:', error);
        setSubmitError('Failed to load existing mappings');
      } finally {
        setLoadingMappings(false);
      }
    };

    loadExistingMappings();
  }, [hwid, modelId, tags]);

  // Get unmapped tags for a camera
  const getUnmappedTags = useCallback((excludedTagId = null) => {
    const mappedTagIds = Object.values(mappings)
      .filter(tag => tag && tag.id !== excludedTagId)
      .map(tag => tag.id);
    
    return tags.filter(tag => !mappedTagIds.includes(tag.id));
  }, [mappings, tags]);

  // Handle camera-tag mapping
  const handleMapCamera = useCallback((cameraId, selectedTag) => {
    console.log(`🎯 handleMapCamera called for ${cameraId}:`, selectedTag);
    
    if (!selectedTag) {
      console.log(`Removing mapping for ${cameraId}`);
      setErrors(prev => ({
        ...prev,
        [cameraId]: ''
      }));
      setMappings(prev => ({
        ...prev,
        [cameraId]: null
      }));
      return;
    }

    console.log(`Setting mapping for ${cameraId}:`, selectedTag);
    setMappings(prev => {
      const updated = {
        ...prev,
        [cameraId]: selectedTag
      };
      console.log(`New mappings state:`, updated);
      return updated;
    });

    // Clear error for this camera
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[cameraId];
      return newErrors;
    });
  }, []);

  // Validate mappings
  const validateMappings = () => {
    const newErrors = {};

    // Count how many cameras are actually mapped
    const mappedCount = Object.values(mappings).filter(Boolean).length;

    // Rule 1: At least one mapping required
    if (mappedCount === 0) {
      setSubmitError('Please map at least one camera before submitting');
      return false;
    }

    // Optional: only validate cameras that have an entry but no tag
    cameras?.forEach(camera => {
      if (
        mappings.hasOwnProperty(camera.camera_id) &&
        !mappings[camera.camera_id]
      ) {
        newErrors[camera.camera_id] = 'Please select a valid tag';
      }
    });

    setErrors(newErrors);
    return true;
  };

  // Get camera description based on status
  const getCameraDescription = (cameraId) => {
    const cameraById = Object.fromEntries(
      cameras.map(cam => [cam.camera_id, cam])
    );
    const camera = cameraById[cameraId];
    if (!camera) return 'Unknown Camera';
    return camera?.camera_status === 1 ? 'Online' : camera?.camera_status === 0 ? 'Offline' : 'Unknown';
  }

  // Get camera status color
  const getCameraStatusColor = (cameraId) => {
    const cameraById = Object.fromEntries(
      cameras.map(cam => [cam.camera_id, cam])
    );
    const camera = cameraById[cameraId];
    if (!camera) return '#808080';
    return camera?.camera_status === 1 ? '#39FF14' : camera?.camera_status === 0 ? '#FF6347' : '#808080';
  }

  // Check if mappings have changed
  const hasMappingsChanged = useCallback(() => {
    const currentMappedIds = Object.entries(mappings)
      .filter(([, tag]) => tag)
      .map(([cameraId, tag]) => `${cameraId}:${tag.id}`)
      .sort();

    const originalMappedIds = Object.entries(originalMappings)
      .filter(([, tag]) => tag)
      .map(([cameraId, tag]) => `${cameraId}:${tag.id}`)
      .sort();

    return JSON.stringify(currentMappedIds) !== JSON.stringify(originalMappedIds);
  }, [mappings, originalMappings]);

  // Handle form submission
  const handleSubmit = async () => {
    setSubmitError('');
    setSuccessMessage('');

    if (!validateMappings()) {
      setSubmitError('Please map all cameras before submitting');
      return;
    }

    setLoading(true);

    try {
      // Build camera lookup for performance
      const cameraById = Object.fromEntries(
        cameras.map(cam => [cam.camera_id, cam])
      );

      const payload = {
        hwid,
        modelId,
        mappings: Object.entries(mappings)
        .filter(([, tag]) => tag) // Only include mapped cameras
        .map(([cameraId, tag]) => {
          const camera = cameraById[cameraId];

          return {
            camera_id: cameraId,
            camera_status: camera?.camera_status || 'unknown',
            tag_id: tag.id,
            tag_label: camera?.camera_name ? camera.camera_name : tag.label,
            tag_color: camera?.camera_status === 1 ? '#32CD32' : camera?.camera_status === 0 ? '#FF6347' : '#808080',
            tag_description: camera?.camera_status === 1 ? 'Online' : camera?.camera_status === 0 ? 'Offline' : 'Unknown',
          };
        })
      };

      console.log('📤 Submitting payload:', payload);

      const response = await API.post(
        '/matterport/camera-tag-mappings',
        payload
      );

      const result = response?.data;
      if (!result) {
        throw new Error('Failed to save mappings');
      }
     
      if (result.dbFailures && result.dbFailures > 0) {
        throw new Error(
          `${result.dbFailures} mappings failed to save. Please retry.`
        );
      }
     
      if (result.matterportFailures && result.matterportFailures > 0) {
        setSuccessMessage(
          `Mappings saved, but ${result.matterportFailures} tag(s) could not be updated in Matterport.`
        );

        console.warn(
          'Matterport patch failures:',
          result.errors?.matterport
        );
      } else {      
        setSuccessMessage(
          'Camera-tag mappings saved and tags updated successfully!'
        );
      }

      // Update original mappings after successful save
      setOriginalMappings(JSON.parse(JSON.stringify(mappings)));

      onSubmitSuccess?.(result);

      // Close modal after success
      setTimeout(() => {
        onModalClose?.();
      }, 1500);

    } catch (error) {
      console.error('Mapping submission error:', error);
      setSubmitError(
        error?.response?.data?.message ||
        error.message ||
        'An error occurred while saving mappings'
      );
    } finally {
      setLoading(false);
    }
  };

  // Calculate completion percentage
  const completionPercentage = React.useMemo(() => {
    if (!cameras || cameras.length === 0) {
      return 0;
    }
    
    const mappedCount = Object.keys(mappings).filter(key => {
      const mapping = mappings[key];
      return mapping && mapping.id;
    }).length;
    
    const percentage = Math.round((mappedCount / cameras.length) * 100);
    return percentage;
  }, [mappings, cameras]);

  // Show loading state while fetching existing mappings
  if (loadingMappings) {
    return (
      <div className="ctm-loading-state">
        <i className="fa-solid fa-spinner fa-spin"></i>
        <p>Loading existing mappings...</p>
      </div>
    );
  }

  if (cameras?.length === 0) {
    return (
      <div className="ctm-empty-state">
        <i className="fa-solid fa-camera"></i>
        <h3>No Cameras Available</h3>
        <p>There are no cameras to map for this model</p>
      </div>
    );
  }

  if (tags.length === 0) {
    return (
      <div className="ctm-empty-state">
        <i className="fa-solid fa-tags"></i>
        <h3>No Tags Available</h3>
        <p>
          There are no tags available for mapping. First create tags on Matterport cloud portal -
          <a href='https://my.matterport.com/dashboard' onClick={ onModalClose } target="_blank" rel="noopener noreferrer" style={{ marginLeft: '0.5rem' }}>Matterport Dashboard</a>
          {' '}and wait upto five minutes before initiating camera mapping.
          
        </p>
      </div>
    );
  }

  return (
    <div className="ctm-container">
      {/* Header Section */}
      <div className="ctm-header">
        <div className="ctm-header-content">
          <p>Map each camera to a tag to organize and categorize your devices</p>
          {/* Show if editing existing mappings */}
          {Object.keys(originalMappings).length > 0 && (
            <div className="ctm-header-note">             
              <span>Editing existing mappings</span>
            </div>
          )}
        </div>
        <div className="ctm-progress">
          <div className="ctm-progress-bar">
            <div 
              className="ctm-progress-fill" 
              style={{ width: `${completionPercentage}%` }}
            />
          </div>
          <span className="ctm-progress-text">
            {Object.keys(mappings).filter(key => mappings[key]).length} / {cameras?.length}
          </span>
        </div>
      </div>

      {/* Messages */}
      {submitError && (
        <div className="ctm-alert ctm-alert-error">
          <i className="fa-solid fa-circle-exclamation"></i>
          {submitError}
        </div>
      )}

      {successMessage && (
        <div className="ctm-alert ctm-alert-success">
          <i className="fa-solid fa-check-circle"></i>
          {successMessage}
        </div>
      )}

      {/* Mapping Cards */}
      <div className="ctm-cards-grid">
        {cameras?.map(camera => {
          const currentMapping = mappings[camera.camera_id];
          const originalMapping = originalMappings[camera.camera_id];
          const excludedTagId = currentMapping?.id;
          const unmappedTags = getUnmappedTags(excludedTagId);
          const hasError = errors[camera.camera_id];
          const isMapped = currentMapping && currentMapping.id;
          const isModified = isMapped !== (originalMapping && originalMapping.id); // Changed state

          return (
            <div 
              key={camera.camera_id} 
              className={`ctm-card ${isModified ? 'ctm-card-modified' : ''}`}
            >
              {/* Camera Info */}
              <div className="ctm-card-camera">
                <div className="ctm-camera-icon">
                  <i className="fa-solid fa-camera"></i>
                </div>
                <div className="ctm-camera-info">
                  <h4>{camera.camera_name}</h4>
                  <code className="ctm-camera-id">{camera.camera_id}</code>
                </div>
              </div>

              {/* Tag Selector */}
              <div className="ctm-tag-selector">
                {!isMapped ? (
                  <select
                    key={`select-${camera.camera_id}`}
                    className={`ctm-select ${hasError ? 'ctm-select-error' : ''}`}
                    value={mappings[camera.camera_id]?.id || ""}
                    onChange={(e) => {
                      const selectedTagId = e.target.value;
                      
                      if (selectedTagId && selectedTagId !== "") {
                        const selectedTag = tags.find(t => t.id === selectedTagId);                       
                        
                        if (selectedTag) {
                          handleMapCamera(camera.camera_id, selectedTag);
                        } else {
                          console.warn(`Tag with ID "${selectedTagId}" not found`);
                        }
                      }
                    }}
                  >
                    <option value="">Choose a tag...</option>
                    {unmappedTags.map(tag => (
                      <option key={tag.id} value={tag.id}>
                        {tag.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <>
                    <div className="ctm-selected-tag">
                      <div className="ctm-tag-color-dot" style={{ backgroundColor: getCameraStatusColor(camera.camera_id) }} />
                      <div className="ctm-selected-tag-info">
                        <p className="ctm-selected-tag-label">{currentMapping.label}</p>
                        <p className="ctm-selected-tag-description">{getCameraDescription(camera.camera_id)}</p>
                      </div>
                      <button
                        className="ctm-remove-tag"
                        onClick={() => handleMapCamera(camera.camera_id, null)}
                        aria-label="Remove tag mapping"
                        title="Remove this mapping"
                      >
                        <i className="fa-solid fa-times"></i>
                      </button>
                    </div>
                  </>
                )}
                {hasError && (
                  <div className="ctm-field-error">
                    <i className="fa-solid fa-triangle-exclamation"></i>
                    {hasError}
                  </div>
                )}
              </div>

              {/* Status Indicator */}
              <div className="ctm-card-status">
                {isMapped ? (
                  <span className="ctm-status-mapped">
                    <i className="fa-solid fa-check"></i>
                    {isModified && originalMapping ? 'Updated' : 'Mapped'}
                  </span>
                ) : (
                  <span className="ctm-status-unmapped">
                    <i className="fa-solid fa-clock"></i>
                    Pending
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer Actions */}
      <div className="ctm-footer">
        <div className="ctm-footer-info">
          <p>
            <strong>{Object.keys(mappings).filter(key => mappings[key]).length}</strong> of <strong>{cameras?.length}</strong> cameras mapped
          </p>
          {hasMappingsChanged() && (
            <p className="ctm-footer-info-modified">
              <i className="fa-solid fa-circle-info"></i>
              Changes detected - click Save to update
            </p>
          )}
        </div>
        <div className="ctm-footer-actions">
          <Button 
            variant="secondary" 
            onClick={() => {
              // Reset to original mappings
              setMappings(JSON.parse(JSON.stringify(originalMappings)));
              setErrors({});
              setSubmitError('');
            }}
          >
            <i className="fa-solid fa-rotate-left"></i>
            Reset
          </Button>
          <Button 
            variant="gradient" 
            onClick={handleSubmit}
            disabled={loading || completionPercentage === 0}
            block
          >
            {loading ? (
              <>
                <i className="fa-solid fa-spinner fa-spin"></i>
                Saving...
              </>
            ) : (
              <>
                <i className="fa-solid fa-check"></i>
                {Object.keys(originalMappings).length > 0 ? 'Update Mappings' : 'Submit Mappings'}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CameraTagMapping;